import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { Kicker } from './Kicker'
import {
  lmsValueAtZ, WHO_WFA_BOYS, WHO_WFA_GIRLS,
  WHO_LFA_BOYS, WHO_LFA_GIRLS, WHO_HCFA_BOYS, WHO_HCFA_GIRLS,
  zScore, zToPercentile, P3, P15, P50, P85, P97, type LmsRow,
} from '@/lib/who/lms'

/**
 * WHO Growth Chart — SVG line chart with WHO percentile bands (3rd, 15th, 50th,
 * 85th, 97th) + baby's own data points overlaid. Currently supports weight-for-age
 * (0-24 months); length + head-circumference render raw data only until we bundle
 * their LMS tables.
 *
 * Not a clinical tool — pediatricians should use WHO Anthro / CDC directly.
 * Documented in the "About" note under the chart.
 */

export type MeasurementKind = 'weight' | 'height' | 'head_circumference'
export type Sex = 'female' | 'male' | 'intersex' | 'unspecified' | null

export interface GrowthPoint {
  /** Age at measurement, months (fractional OK) */
  ageMonths: number
  /** Value in canonical unit: kg for weight, cm for height/head */
  value: number
  /** Original measurement timestamp for label */
  at: string
}

interface Props {
  kind: MeasurementKind
  sex: Sex
  points: GrowthPoint[]
  height?: number
}

export function WhoChart({ kind, sex, points, height = 260 }: Props) {
  const unit = kind === 'weight' ? 'kg' : 'cm'
  const label = kind === 'weight' ? 'Weight-for-age' : kind === 'height' ? 'Length-for-age' : 'Head circumference-for-age'

  const table = useMemo<LmsRow[] | null>(() => {
    if (sex !== 'female' && sex !== 'male') return null // unspecified
    if (kind === 'weight')            return sex === 'female' ? WHO_WFA_GIRLS  : WHO_WFA_BOYS
    if (kind === 'height')            return sex === 'female' ? WHO_LFA_GIRLS  : WHO_LFA_BOYS
    if (kind === 'head_circumference') return sex === 'female' ? WHO_HCFA_GIRLS : WHO_HCFA_BOYS
    return null
  }, [kind, sex])

  // Fixed 0..24 months x-axis
  const xMin = 0, xMax = 24
  // Y-axis: fit to combined range of data + reference (if present)
  const yRange = useMemo(() => {
    let lo = Infinity, hi = -Infinity
    if (table) {
      for (const r of table) {
        const p3 = lmsValueAtZ(r, P3)
        const p97 = lmsValueAtZ(r, P97)
        lo = Math.min(lo, p3)
        hi = Math.max(hi, p97)
      }
    }
    for (const p of points) {
      if (p.ageMonths >= xMin && p.ageMonths <= xMax) {
        lo = Math.min(lo, p.value)
        hi = Math.max(hi, p.value)
      }
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
      lo = 0; hi = kind === 'weight' ? 15 : 100
    }
    const pad = (hi - lo) * 0.1
    return { lo: Math.max(0, lo - pad), hi: hi + pad }
  }, [table, points, kind])

  const W = 320, H = height, padL = 34, padR = 12, padT = 16, padB = 26
  const plotW = W - padL - padR
  const plotH = H - padT - padB
  const x = (m: number) => padL + ((m - xMin) / (xMax - xMin)) * plotW
  const y = (v: number) => padT + (1 - (v - yRange.lo) / (yRange.hi - yRange.lo)) * plotH

  const bandPath = (zLo: number, zHi: number): string => {
    if (!table) return ''
    const top: string[] = []
    const bot: string[] = []
    for (const r of table) {
      top.push(`${x(r.months)},${y(lmsValueAtZ(r, zHi))}`)
      bot.push(`${x(r.months)},${y(lmsValueAtZ(r, zLo))}`)
    }
    return `M${top.join(' L')} L${bot.reverse().join(' L')} Z`
  }

  const linePath = (z: number): string => {
    if (!table) return ''
    return 'M' + table.map((r) => `${x(r.months)},${y(lmsValueAtZ(r, z))}`).join(' L')
  }

  const yTicks = 4
  const yTickVals: number[] = []
  for (let i = 0; i <= yTicks; i++) yTickVals.push(yRange.lo + ((yRange.hi - yRange.lo) * i) / yTicks)

  const latest = points[points.length - 1]
  const latestPct = latest && table ? zToPercentile(zScore(table, latest.ageMonths, latest.value) ?? 0) : null

  return (
    <div className="glass-tile p-4">
      <div className="flex items-baseline justify-between">
        <Kicker>{label}</Kicker>
        {latest && (
          <div className="text-caption text-text-secondary">
            {latest.value.toFixed(kind === 'weight' ? 2 : 1)} {unit}
            {latestPct != null && <span className="ml-2 text-text-tertiary">({latestPct.toFixed(0)}%ile)</span>}
          </div>
        )}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="mt-3 block w-full h-auto"
        role="img"
        aria-label={`${label} chart`}
      >
        {/* Y-axis labels */}
        {yTickVals.map((v, i) => (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke="currentColor" strokeOpacity={0.06} />
            <text x={padL - 4} y={y(v) + 3} fontSize="9" textAnchor="end" fill="currentColor" fillOpacity={0.6}>
              {v.toFixed(kind === 'weight' ? 1 : 0)}
            </text>
          </g>
        ))}

        {/* X-axis labels every 3 months */}
        {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((m) => (
          <g key={m}>
            <line x1={x(m)} x2={x(m)} y1={padT} y2={H - padB} stroke="currentColor" strokeOpacity={0.04} />
            <text x={x(m)} y={H - padB + 12} fontSize="9" textAnchor="middle" fill="currentColor" fillOpacity={0.6}>
              {m}m
            </text>
          </g>
        ))}

        {/* WHO percentile bands (only when we have LMS data) */}
        {table && (
          <g>
            <path d={bandPath(P3, P97)} fill="rgba(46,210,163,0.06)" />
            <path d={bandPath(P15, P85)} fill="rgba(46,210,163,0.12)" />
            <path d={linePath(P50)} stroke="rgba(46,210,163,0.85)" strokeWidth={1.5} fill="none" strokeDasharray="4 3" />
            <path d={linePath(P3)}  stroke="rgba(46,210,163,0.55)" strokeWidth={1} fill="none" />
            <path d={linePath(P97)} stroke="rgba(46,210,163,0.55)" strokeWidth={1} fill="none" />
          </g>
        )}

        {/* Baby data-point line */}
        {points.length > 1 && (
          <motion.path
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
            d={'M' + points.filter((p) => p.ageMonths >= xMin && p.ageMonths <= xMax).map((p) => `${x(p.ageMonths)},${y(p.value)}`).join(' L')}
            stroke="#FF7A2D"
            strokeWidth={2.2}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {/* Baby data-point dots */}
        {points.filter((p) => p.ageMonths >= xMin && p.ageMonths <= xMax).map((p, i) => (
          <motion.circle
            key={i}
            cx={x(p.ageMonths)}
            cy={y(p.value)}
            r={4}
            fill="#FF7A2D"
            stroke="white"
            strokeWidth={1.5}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.32, ease: [0.34, 1.56, 0.64, 1], delay: i * 0.04 }}
          />
        ))}
      </svg>

      {/* Legend */}
      <div className="mt-2 flex flex-wrap gap-3 text-tag text-text-tertiary">
        {table && (
          <>
            <LegendDot color="rgba(46,210,163,0.85)" label="50th %ile (median)" dashed />
            <LegendDot color="rgba(46,210,163,0.55)" label="3rd / 97th %ile" />
          </>
        )}
        <LegendDot color="#FF7A2D" label="Your baby" />
      </div>

      {!table && (
        <div className="mt-3 rounded-r-sm p-3 text-tag text-text-tertiary" style={{ background: 'rgb(var(--bg-elevated) / 0.6)' }}>
          Sex not set on this person — showing raw data without WHO percentile bands. Edit the person to set sex.
        </div>
      )}

      <div className="mt-2 text-tag text-text-quiet">
        WHO Child Growth Standards, 0-24 months. For parent trending — not for clinical diagnosis.
      </div>
    </div>
  )
}

function LegendDot({ color, label, dashed = false }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="inline-block w-4"
        style={{
          height: dashed ? 0 : 2,
          background: dashed ? 'transparent' : color,
          borderTop: dashed ? `2px dashed ${color}` : undefined,
        }}
      />
      {label}
    </span>
  )
}
